import Chat from '@components/AnalystChat/Chat';
import QuickHelpPanel from '@components/QuickHelpPanel/QuickHelpPanel';
import { Page } from '@components/common/Page';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { Box, Center } from '@mantine/core';
import { useQuickHelpContext } from '@providers/QuickHelp';
import useAiAnalystContext from '@providers/AiAnalyst/useAiAnalystContext';
import { type FC, type PropsWithChildren } from 'react';
import { useLocation } from 'react-router';
import SideNavigationBar from './SideNavigationBar';

/**
 * Routes where the Quick Help AI side panel is available.
 * Alerts (/alerts) and Replenishment (/replenishment) have dedicated AI experiences.
 */
const QUICK_HELP_ROUTES = [
    '/home',
    '/forecasts',
    '/inventory',
    '/suppliers',
    '/purchase-orders',
    '/sales-orders',
    '/alert/', // alert detail/checklist pages
];

/** Replenishment keeps the old Chat component for now */
const LEGACY_CHAT_ROUTES = ['/replenishment'];

type Props = {
    title?: string;
    backgroundColor?: string;
};

const AppLayout: FC<PropsWithChildren<Props>> = ({
    title,
    backgroundColor,
    children,
}) => {
    const { currentUser } = useCurrentUser();
    const isConsultant = currentUser?.role === 'CONSULTANT';
    const { pathname } = useLocation();
    const { state: quickHelpState } = useQuickHelpContext();
    const { showAISideBar } = useAiAnalystContext((ctx) => ctx.state);

    const isQuickHelpRoute = QUICK_HELP_ROUTES.some((r) => pathname.startsWith(r));
    const isLegacyChatRoute = LEGACY_CHAT_ROUTES.some((r) => pathname.startsWith(r));

    if (isConsultant) {
        return (
            <Center className="h-screen w-full" bg={backgroundColor ?? 'white'}>
                {children}
            </Center>
        );
    }

    return (
        <Page
            title={title}
            backgroundColor={backgroundColor ?? 'white'}
            withPadding={false}
            leftSidebar={<SideNavigationBar />}
            rightSidebar={
                isQuickHelpRoute
                    ? <QuickHelpPanel />
                    : isLegacyChatRoute
                    ? <Chat />
                    : undefined
            }
            isRightSidebarOpen={
                isQuickHelpRoute
                    ? quickHelpState.isOpen
                    : isLegacyChatRoute
                    ? showAISideBar
                    : false
            }
            rightSidebarWidthProps={{
                defaultWidth: 400,
                minWidth: 320,
                maxWidth: 500,
            }}
        >
            <Box className="h-full w-full">{children}</Box>
        </Page>
    );
};

export default AppLayout;
